/**
 * Fax letters attached to pending J-codes by fax intake, and the Drugs-tab document icon they drive.
 * @module faxLetterDocument
 */
import {
  FaxLetterReviewStatus,
  OrderFaxLetter,
} from "../../../../../api/postCall/faxLetter";
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { AuthLetterReviewStatus } from "../../../../../enums/authLetterReviewStatus";

/**
 * Indexes an order's fax letters by J-code, keeping the newest letter per J-code.
 *
 * @param letters - The order's attached letters, newest first (as the API returns them)
 * @returns J-code row id → its newest letter
 */
export const latestFaxLetterByJCodeId = (
  letters: OrderFaxLetter[],
): Map<string, OrderFaxLetter> => {
  const byJCode = new Map<string, OrderFaxLetter>();
  letters.forEach((letter) => {
    if (!byJCode.has(letter.j_code_id)) byJCode.set(letter.j_code_id, letter);
  });
  return byJCode;
};

/**
 * Shows the document icon whenever the drug has an attached fax letter.
 *
 * Dark green while the letter awaits review, light green once reviewed (the Auth on File
 * reviewed-and-saved colour). All attached letters remain accessible after the drug status changes.
 *
 * @param row - A Drugs-tab row
 * @param letter - The J-code's newest fax letter, if any
 * @returns The row with its `docWithUploadIcon` cell set for the letter
 */
export const applyFaxLetterIcon = (
  row: CptCodesTableRowModel,
  letter: OrderFaxLetter | undefined,
): CptCodesTableRowModel => {
  if (!letter) {
    return row;
  }
  return {
    ...row,
    docWithUploadIcon: {
      ...row.docWithUploadIcon,
      isVisible: true,
      isUploaded: true,
      authLetterReviewStatus:
        letter.review_status === FaxLetterReviewStatus.Reviewed
          ? AuthLetterReviewStatus.ReviewedAndSaved
          : undefined,
    },
  };
};

/**
 * Route to the Authorization Details page for one fax letter on a J-code.
 *
 * @param orderId - Order the J-code belongs to
 * @param jCodeId - J-code record id
 * @param documentId - The fax letter's attachment id
 * @returns The app-relative path
 */
export const faxLetterDetailsPath = (
  orderId: string,
  jCodeId: string,
  documentId: string,
): string =>
  `/worklists/authorisation-details/${encodeURIComponent(orderId)}/fax-letter/${encodeURIComponent(jCodeId)}/${encodeURIComponent(documentId)}`;
